import {
    type ApiOrder,
    type EnumMaterialFlow,
    type EnumStagingStatus,
    getEnumMaterialFlowLabel,
    materialFlowOptions,
    processStateOptions,
} from '@Model';
import { type Awaitable, Badge, type Column } from '@synqo/frontend-component-library';
import { getBadgeVariantByStagingStatus, getEnumStagingStatusLabel } from '@Model';
import { AUFTRAG_API } from '@Constants/backendConstants';
import { LinkableAddressCell, LinkedPositionsCell } from '@Components/Cells';
import { useMemo } from 'react';
import { containerActionsColumn } from './containerActionsColumn';

const getOrdersBaseObjectPath = (orderId: string) => `${AUFTRAG_API}/orders/${orderId}`;

interface GetStagedOrderColumnsOptions {
    onLinked: () => Awaitable<void>;
}

export const getStagedOrderColumns = ({ onLinked }: GetStagedOrderColumnsOptions): Column<ApiOrder>[] => [
    { name: 'created_at', label: 'Empfangen am', type: 'date' },
    { name: 'created_by', label: 'Empfangen von' },
    {
        name: 'staging_status',
        label: 'Import-Status',
        filterVariant: 'select',
        filterOptions: processStateOptions,
        renderCell: ({ cellData }) => (
            <Badge variant={getBadgeVariantByStagingStatus(cellData as EnumStagingStatus | undefined)}>
                {getEnumStagingStatusLabel(cellData as EnumStagingStatus | undefined)}
            </Badge>
        ),
    },
    { name: 'number', label: 'Nummer' },
    {
        name: 'material_flow',
        label: 'Stoffweg',
        filterVariant: 'select',
        filterOptions: materialFlowOptions,
        renderCell: ({ cellData }) => getEnumMaterialFlowLabel(cellData as EnumMaterialFlow),
    },
    {
        name: 'customer.display_string',
        label: 'Kunde',
        renderCell: ({ rowData }) => {
            const stagedOrder = rowData as ApiOrder;
            const baseApiObjectPath = getOrdersBaseObjectPath(stagedOrder.id);

            return (
                <LinkableAddressCell<ApiOrder>
                    rootObject={stagedOrder}
                    baseApiObjectPath={baseApiObjectPath}
                    propertyName="customer"
                    link={stagedOrder.customer}
                    onLinked={onLinked}
                />
            );
        },
    },
    {
        name: 'site.display_string',
        label: 'Lieferanschrift',
        renderCell: ({ rowData }) => {
            const stagedOrder = rowData as ApiOrder;
            const baseApiObjectPath = getOrdersBaseObjectPath(stagedOrder.id);

            return (
                <LinkableAddressCell<ApiOrder>
                    rootObject={stagedOrder}
                    baseApiObjectPath={baseApiObjectPath}
                    propertyName="site"
                    link={stagedOrder.site.address}
                    onLinked={onLinked}
                />
            );
        },
    },
    {
        name: 'positions',
        label: 'Leistungen',
        renderCell: ({ rowData }) => (
            <LinkedPositionsCell positions={rowData.positions} containerActions={rowData.container_actions} />
        ),
        disableColumnSort: true,
    },
    containerActionsColumn,
];

export const useStagedOrderColumns = ({ onLinked }: GetStagedOrderColumnsOptions) => {
    return useMemo(() => getStagedOrderColumns({ onLinked }), [onLinked]);
};
